import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Webhook, CheckCircle2, XCircle, Send, Lock, Trash2, Mail, Plus, X as XIcon } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

function relTime(iso) {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
}

function StatCard({ label, value, tone = "default", testid }) {
  const toneCls = {
    default: "text-white",
    success: "text-emerald-400",
    error: "text-red-400",
    warning: "text-amber-400",
    muted: "text-zinc-400",
  }[tone];
  return (
    <div data-testid={testid} className="border border-zinc-800 bg-[#0A0A0A] p-4 rounded-sm">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-2 font-mono font-semibold tracking-tight text-2xl md:text-3xl ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function Integrations() {
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const [headerName, setHeaderName] = useState("X-Api-Key");
  const [secret, setSecret] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [verifyValue, setVerifyValue] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const { data: config } = useQuery({
    queryKey: ["webhook-config"],
    queryFn: async () => (await api.get("/webhook/config")).data,
  });

  const { data: stats } = useQuery({
    queryKey: ["webhook-stats"],
    queryFn: async () => (await api.get("/webhook/stats")).data,
    refetchInterval: 10000,
  });

  useEffect(() => {
    if (!config) return;
    setUrl(config.url || "");
    setHeaderName(config.auth_header_name || "X-Api-Key");
    setEnabled(!!config.enabled);
  }, [config]);

  const save = useMutation({
    mutationFn: async (payload) => (await api.put("/webhook/config", payload)).data,
    onSuccess: () => {
      toast.success("Webhook config saved");
      setSecret("");
      qc.invalidateQueries({ queryKey: ["webhook-config"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/webhook/test");
      setTestResult(data);
      if (data.ok) toast.success("Test delivery successful");
      else toast.error(`Test failed: ${data.error || `HTTP ${data.http_status}`}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = { enabled, url, auth_header_name: headerName };
    if (secret) payload.auth_header_value = secret;
    save.mutate(payload);
  };

  const clearSecret = () => {
    if (!confirm("Remove the stored secret? No auth header will be sent to the webhook after this. You can re-enter a new secret anytime.")) return;
    save.mutate({ clear_auth_header_value: true });
  };

  const verify = async () => {
    if (!verifyValue) return;
    setVerifying(true); setVerifyResult(null);
    try {
      const { data } = await api.post("/webhook/verify-secret", { value: verifyValue });
      setVerifyResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const toggleEnabled = (v) => {
    setEnabled(v);
    // Save immediately so the flag is not lost
    const payload = { enabled: v };
    save.mutate(payload);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="integrations-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Integrations</div>
        <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">Webhook Forwarding</h1>
        <p className="text-sm text-zinc-400 mt-1">Forward every new news item to an external endpoint, with delivery tracking and exactly-once guarantees.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Delivered 24h" value={stats?.delivered_24h ?? "…"} tone="success" testid="metric-delivered-24h" />
        <StatCard label="Total delivered" value={stats?.delivered ?? "…"} testid="metric-delivered" />
        <StatCard label="Pending" value={stats?.pending ?? "…"} tone="warning" testid="metric-pending" />
        <StatCard label="Gave up" value={stats?.given_up ?? "…"} tone="error" testid="metric-given-up" />
        <StatCard label="Rejected 4xx" value={stats?.unrecoverable_4xx ?? "…"} tone="muted" testid="metric-unrecoverable" />
      </div>

      {/* Config */}
      <form onSubmit={submit} className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-6 space-y-5">
        <div className="flex items-center justify-between pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Webhook className="h-5 w-5 text-zinc-400" />
            <div>
              <div className="font-mono text-sm">Enable webhook forwarding</div>
              <div className="text-[11px] font-mono text-zinc-500">When ON, every new news item is POST-ed to the URL below.</div>
            </div>
          </div>
          <Switch data-testid="webhook-enabled-switch" checked={enabled} onCheckedChange={toggleEnabled} />
        </div>

        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Destination URL</Label>
          <Input
            data-testid="webhook-url-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
            placeholder="https://n8n.example.com/webhook/rss-ingest"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Header name</Label>
            <Input
              data-testid="webhook-header-name-input"
              value={headerName}
              onChange={(e) => setHeaderName(e.target.value)}
              className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
              placeholder="X-Api-Key"
            />
            <div className="mt-1 text-[10px] font-mono text-zinc-500">HTTP header key that n8n expects</div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              Header value (the secret)
              {config?.has_secret ? (
                <Badge className="rounded-sm bg-transparent border border-emerald-500/60 text-emerald-400 font-mono text-[9px]">
                  <Lock className="h-3 w-3 mr-1" /> SET · {config.secret_length} chars
                </Badge>
              ) : (
                <Badge className="rounded-sm bg-transparent border border-red-500/60 text-red-400 font-mono text-[9px]">EMPTY</Badge>
              )}
            </Label>
            <Input
              data-testid="webhook-secret-input"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
              placeholder={config?.has_secret ? "•••••••••  (leave empty to keep current)" : "Enter the secret VALUE (not the name)"}
              autoComplete="new-password"
            />
            <div className="mt-1 flex items-center justify-between">
              <div className="text-[10px] font-mono text-zinc-500">
                Saved value is trimmed of whitespace automatically. Never returned via API.
                {config?.secret_fingerprint && (
                  <> Fingerprint: <span className="text-zinc-300">sha256[:12]={config.secret_fingerprint}</span></>
                )}
              </div>
              {config?.has_secret && (
                <button
                  data-testid="clear-secret-btn"
                  type="button"
                  onClick={clearSecret}
                  className="text-[10px] font-mono text-red-400 hover:text-red-300 inline-flex items-center gap-1 shrink-0"
                >
                  <Trash2 className="h-3 w-3" /> CLEAR STORED SECRET
                </button>
              )}
            </div>
          </div>
        </div>

        {/* n8n help block */}
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 text-[11px] font-mono">
          <div className="text-amber-400 font-semibold mb-1">n8n Header Auth reminder</div>
          <div className="text-zinc-400 leading-relaxed">
            In n8n's "Header Auth" credential you have two fields: <span className="text-white">Name</span> and <span className="text-white">Value</span>. BOTH must match what we send.
            If n8n has <code className="text-white">Name = secret_key_rss</code>, our "Header name" field above must also be <code className="text-white">secret_key_rss</code>,
            AND our "Header value" must be exactly the same value n8n has stored. A 403 means one of the two doesn't match.
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 pt-4 border-t border-zinc-800">
          <Button data-testid="save-webhook-btn" type="submit" disabled={save.isPending} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono">
            {save.isPending ? "SAVING…" : "SAVE CHANGES"}
          </Button>
          <Button
            data-testid="test-webhook-btn"
            type="button"
            variant="outline"
            className="rounded-sm border-zinc-800 bg-transparent font-mono gap-2"
            onClick={test}
            disabled={testing || !config?.url}
          >
            <Send className="h-4 w-4" /> {testing ? "SENDING TEST…" : "SEND TEST EVENT"}
          </Button>
        </div>

        {testResult && (
          <div
            data-testid="test-result-box"
            className={`border rounded-sm p-3 text-xs font-mono ${
              testResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              {testResult.ok ? (
                <><CheckCircle2 className="h-4 w-4 text-emerald-400" /> <span className="text-emerald-400">Test OK</span></>
              ) : (
                <><XCircle className="h-4 w-4 text-red-400" /> <span className="text-red-400">Test failed</span></>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-zinc-400">
              <span>http_status</span><span className="text-white text-right">{testResult.http_status ?? "—"}</span>
              <span>error</span><span className="text-white text-right truncate" title={testResult.error}>{testResult.error || "—"}</span>
              <span>sent_to</span><span className="text-white text-right truncate" title={testResult.sent_to}>{testResult.sent_to}</span>
              <span>header_name_used</span><span className="text-white text-right">{testResult.header_name}</span>
              <span>auth_header_sent</span>
              <span className={`text-right ${testResult.auth_header_sent ? "text-emerald-400" : "text-red-400"}`}>
                {testResult.auth_header_sent ? `YES (value length: ${testResult.auth_header_value_length})` : "NO — value field is empty!"}
              </span>
              {testResult.response_snippet && (
                <>
                  <span>response_body</span>
                  <span className="text-zinc-300 text-right truncate" title={testResult.response_snippet}>{testResult.response_snippet.slice(0, 80)}</span>
                </>
              )}
            </div>
            {testResult.http_status === 403 && (
              <div className="mt-2 pt-2 border-t border-zinc-800 text-amber-400 text-[11px] leading-relaxed">
                <strong>Diagnosing 403:</strong> {testResult.auth_header_sent
                  ? "n8n received the header but the VALUE doesn't match. Double-check what n8n has stored in its Header Auth credential."
                  : "No auth header was sent (value field is empty). Enter the secret value in the field above and save."}
              </div>
            )}
          </div>
        )}
      </form>

      {/* Verify secret matches n8n */}
      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5" data-testid="verify-secret-block">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Verify secret matches n8n</div>
        <p className="text-[11px] font-mono text-zinc-500 mb-3">
          Paste EXACTLY what you have in n8n's Header Auth "Value" field. We'll compare against our stored secret without exposing either.
          This detects invisible characters (spaces, newlines) that break auth.
        </p>
        <div className="flex gap-2">
          <Input
            data-testid="verify-secret-input"
            type="password"
            value={verifyValue}
            onChange={(e) => setVerifyValue(e.target.value)}
            className="bg-transparent border-zinc-800 rounded-sm font-mono"
            placeholder="Paste the value from n8n's credential"
          />
          <Button
            data-testid="verify-secret-btn"
            type="button"
            onClick={verify}
            disabled={verifying || !verifyValue}
            variant="outline"
            className="rounded-sm border-zinc-800 bg-transparent font-mono"
          >
            {verifying ? "…" : "COMPARE"}
          </Button>
        </div>
        {verifyResult && (
          <div
            data-testid="verify-result-box"
            className={`mt-3 border rounded-sm p-3 text-xs font-mono ${
              verifyResult.matches_exact ? "border-emerald-500/40 bg-emerald-500/5" :
              verifyResult.matches_after_strip ? "border-amber-500/40 bg-amber-500/5" :
              "border-red-500/40 bg-red-500/5"
            }`}
          >
            {verifyResult.matches_exact && (
              <div className="text-emerald-400 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Values match exactly — if n8n still 403s, the issue is elsewhere (workflow not active, wrong credential linked to node…)
              </div>
            )}
            {verifyResult.matches_after_strip && (
              <div className="text-amber-400">
                Values match ONLY after trimming whitespace. Your n8n pasted value has invisible characters (spaces/newlines) at the start/end. Fix in n8n: delete and re-paste the value cleanly.
              </div>
            )}
            {!verifyResult.matches_exact && !verifyResult.matches_after_strip && (
              <div className="text-red-400 flex items-center gap-2">
                <XCircle className="h-4 w-4" /> Values do NOT match. Either update the value here or in n8n so both are identical.
              </div>
            )}
            <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-zinc-400 border-t border-zinc-800 pt-2">
              <span>stored_length</span><span className="text-white text-right">{verifyResult.stored_length}</span>
              <span>submitted_length</span><span className="text-white text-right">{verifyResult.submitted_length}</span>
              {verifyResult.submitted_length !== verifyResult.submitted_length_stripped && (
                <>
                  <span>after_trim_length</span><span className="text-amber-400 text-right">{verifyResult.submitted_length_stripped}</span>
                </>
              )}
              {verifyResult.has_leading_whitespace && (
                <><span>leading_whitespace</span><span className="text-red-400 text-right">YES</span></>
              )}
              {verifyResult.has_trailing_whitespace && (
                <><span>trailing_whitespace</span><span className="text-red-400 text-right">YES</span></>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Health block */}
      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Last activity</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs font-mono">
          <span className="text-zinc-500">Last success</span>
          <span className="text-white text-right">{relTime(config?.last_success_at)}</span>
          <span className="text-zinc-500">Last error</span>
          <span className="text-red-400 text-right">{relTime(config?.last_error_at)}</span>
          {config?.last_error && (
            <>
              <span className="text-zinc-500">Error detail</span>
              <span className="text-red-400 text-right truncate" title={config.last_error}>{config.last_error}</span>
            </>
          )}
          <span className="text-zinc-500">Config updated</span>
          <span className="text-white text-right">{relTime(config?.updated_at)}</span>
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Payload schema</div>
        <pre className="text-[11px] font-mono text-zinc-300 whitespace-pre-wrap leading-relaxed">
{`POST <url>
Headers:
  Content-Type: application/json
  <header_name>: <secret>

Body:
{
  "id": "<feed_id>:<item_hash>",   // idempotency key
  "feed_id": "...",
  "feed_name": "...",
  "title": "...",
  "link": "...",
  "summary": "...",
  "author": "...",
  "published_at": "ISO-8601",
  "fetched_at":   "ISO-8601"
}`}
        </pre>
        <div className="mt-3 text-[11px] font-mono text-zinc-500">
          Retries: up to 5 attempts with backoff. On 2xx → marked delivered. On 4xx (except 429) → marked as unrecoverable, no retry. On 5xx/timeout → retried.
        </div>
      </div>

      <AlertsSection />
    </div>
  );
}

function AlertsSection() {
  const qc = useQueryClient();
  const [newEmail, setNewEmail] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const { data: cfg } = useQuery({
    queryKey: ["alerts-config"],
    queryFn: async () => (await api.get("/alerts/config")).data,
  });

  const save = useMutation({
    mutationFn: async (payload) => (await api.put("/alerts/config", payload)).data,
    onSuccess: () => { toast.success("Alerts config saved"); qc.invalidateQueries({ queryKey: ["alerts-config"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const addRecipient = () => {
    if (!newEmail.includes("@")) { toast.error("Invalid email"); return; }
    const recipients = [...(cfg?.recipients || []), newEmail.trim().toLowerCase()];
    save.mutate({ recipients });
    setNewEmail("");
  };

  const removeRecipient = (email) => {
    const recipients = (cfg?.recipients || []).filter((r) => r !== email);
    save.mutate({ recipients });
  };

  const toggle = (field, value) => {
    save.mutate({ [field]: value });
  };

  const runTest = async () => {
    if (!testTo.includes("@")) { toast.error("Invalid recipient"); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/alerts/test-email", { to: testTo });
      setTestResult(data);
      if (data.ok) toast.success("Test email sent");
      else toast.error(`Failed: ${data.error}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  const mailerReady = !!cfg?.mailer_configured;

  return (
    <div className="space-y-6 pt-6 border-t border-zinc-800" data-testid="alerts-section">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Notifications</div>
        <h2 className="text-xl md:text-2xl font-mono font-semibold tracking-tight">Email Alerts</h2>
        <p className="text-sm text-zinc-400 mt-1">Automatic notifications when the system needs attention. Sent via Resend.</p>
      </div>

      <div className={`border rounded-sm p-4 text-xs font-mono ${mailerReady ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}>
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          {mailerReady ? (
            <span className="text-emerald-400">Resend configured — from: {cfg?.from_name} &lt;{cfg?.from_email}&gt;</span>
          ) : (
            <span className="text-red-400">Resend NOT configured — set RESEND_API_KEY in the environment</span>
          )}
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5 space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Recipients ({cfg?.recipients?.length || 0})</div>
        <div className="space-y-2" data-testid="recipients-list">
          {(cfg?.recipients || []).length === 0 && (
            <div className="text-zinc-500 font-mono text-xs">— no recipients — add one below —</div>
          )}
          {(cfg?.recipients || []).map((r) => (
            <div key={r} className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 py-2">
              <span className="font-mono text-sm">{r}</span>
              <button
                data-testid={`remove-recipient-${r}`}
                onClick={() => removeRecipient(r)}
                className="text-zinc-500 hover:text-red-400"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Input
            data-testid="new-recipient-input"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="alerts@yourteam.com"
            className="bg-transparent border-zinc-800 rounded-sm font-mono"
          />
          <Button data-testid="add-recipient-btn" onClick={addRecipient} className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono gap-1">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5 space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Alert types</div>
        {[
          { key: "scheduler_stalled_enabled", label: "Scheduler stalled", desc: "No tick for more than 3 minutes" },
          { key: "webhook_failing_enabled",   label: "Webhook failing",   desc: "Items with 5+ consecutive delivery failures" },
          { key: "auto_paused_enabled",       label: "Feed auto-paused",  desc: "A feed reached 20 consecutive failures and got paused" },
        ].map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between border-b border-zinc-900 pb-3 last:border-0">
            <div>
              <div className="font-mono text-sm">{label}</div>
              <div className="text-[11px] font-mono text-zinc-500">{desc}</div>
            </div>
            <Switch
              data-testid={`alert-toggle-${key}`}
              checked={!!cfg?.[key]}
              onCheckedChange={(v) => toggle(key, v)}
            />
          </div>
        ))}
        <div className="text-[10px] font-mono text-zinc-500 pt-2">Same alert throttled to 1 email per 30 min max, to prevent flood.</div>
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Test delivery</div>
        <div className="flex gap-2">
          <Input
            data-testid="test-email-input"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            className="bg-transparent border-zinc-800 rounded-sm font-mono"
          />
          <Button
            data-testid="send-test-email-btn"
            onClick={runTest}
            disabled={testing || !mailerReady}
            variant="outline"
            className="rounded-sm border-zinc-800 bg-transparent gap-1"
          >
            <Send className="h-4 w-4" /> {testing ? "SENDING…" : "SEND TEST"}
          </Button>
        </div>
        {testResult && (
          <div className={`mt-3 border rounded-sm p-3 text-xs font-mono ${testResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5"}`}>
            {testResult.ok ? (
              <span className="text-emerald-400 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Email sent — id: {testResult.id}</span>
            ) : (
              <span className="text-red-400 flex items-center gap-2"><XCircle className="h-4 w-4" /> {testResult.error}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
